import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { INK, SUB, FAINT, HAIR, SOFT, MON, PRESETS, ymd, fmtYmd, rangeLabel } from "./erShared";
import { DateBasisButton } from "./DateBasisButton";

const POP = { background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, boxShadow: "0 10px 28px rgba(20,20,18,.14)" };

function Month({ y, m, lo, hi, onPick }) {
  const today = ymd(new Date());
  const first = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells = Array.from({ length: first }, (_, i) => <span key={`b${i}`} />);
  for (let d = 1; d <= days; d++) {
    const iso = ymd(new Date(y, m, d));
    const end = iso === lo || iso === hi, inside = lo && hi && iso > lo && iso < hi, future = iso > today;
    cells.push(
      <button key={iso} type="button" disabled={future} onClick={() => onPick(iso)} data-testid={`er-cal-day-${iso}`}
        className="h-[30px] text-[12.5px] tabular-nums rounded-[7px] disabled:opacity-30 disabled:cursor-not-allowed"
        style={{ background: end ? INK : inside ? SOFT : "transparent", color: end ? "#fff" : INK, fontWeight: end ? 700 : 500, boxShadow: iso === today && !end ? `inset 0 0 0 1px ${HAIR}` : "none" }}>{d}</button>
    );
  }
  return (
    <div className="w-[224px]">
      <div className="text-[13px] font-bold text-center mb-2" style={{ color: INK }}>{MON[m]} {y}</div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {["S", "M", "T", "W", "T", "F", "S"].map((x, i) => <span key={i} className="text-[11px] text-center py-1" style={{ color: FAINT }}>{x}</span>)}
        {cells}
      </div>
    </div>
  );
}

function CalendarPop({ onApply, onCancel }) {
  const [offset, setOffset] = useState(0);
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const t = new Date();
  const cur = new Date(t.getFullYear(), t.getMonth() + offset, 1), prev = new Date(cur.getFullYear(), cur.getMonth() - 1, 1);
  const lo = a && b ? (a < b ? a : b) : a, hi = a && b ? (a < b ? b : a) : null;
  const pick = (iso) => { if (!a || b) { setA(iso); setB(null); } else setB(iso); };
  const IB = "w-[28px] h-[28px] inline-flex items-center justify-center rounded-[8px] disabled:opacity-30";
  return (
    <div className="absolute right-0 z-40 mt-1 p-3" style={POP} data-testid="er-cal">
      <div className="flex items-center mb-1">
        <button type="button" className={IB} style={{ border: `1px solid ${HAIR}` }} onClick={() => setOffset((o) => o - 1)} aria-label="Earlier months" data-testid="er-cal-prev"><ChevronLeft size={15} /></button>
        <span className="flex-1" />
        <button type="button" className={IB} style={{ border: `1px solid ${HAIR}` }} disabled={offset >= 0} onClick={() => setOffset((o) => o + 1)} aria-label="Later months" data-testid="er-cal-next"><ChevronRight size={15} /></button>
      </div>
      <div className="flex gap-5">
        <Month y={prev.getFullYear()} m={prev.getMonth()} lo={lo} hi={hi} onPick={pick} />
        <Month y={cur.getFullYear()} m={cur.getMonth()} lo={lo} hi={hi} onPick={pick} />
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${SOFT}` }}>
        <span className="text-[12px]" style={{ color: SUB }} data-testid="er-cal-text">{lo ? (hi ? `${fmtYmd(lo)} – ${fmtYmd(hi)}` : `${fmtYmd(lo)} – pick an end date`) : "Pick a start date"}</span>
        <span className="flex-1" />
        <button type="button" onClick={onCancel} className="text-[12.5px] font-semibold px-3 h-[30px] rounded-[8px]" style={{ border: `1px solid ${HAIR}`, color: INK, background: "#fff" }} data-testid="er-cal-cancel">Cancel</button>
        <button type="button" disabled={!(lo && hi)} onClick={() => onApply({ kind: "custom", from: lo, to: hi })} className="text-[12.5px] font-semibold px-3 h-[30px] rounded-[8px] disabled:opacity-40" style={{ background: INK, color: "#fff" }} data-testid="er-cal-apply">Apply</button>
      </div>
    </div>
  );
}

export function DateRangePicker({ range, basis = "added", onBasisChange, onChange, inGroup }) {
  const presets = PRESETS;
  const rangeKey = range.kind === "preset" ? range.key : null;
  return (
    <DateBasisButton
      basis={basis} onBasisChange={onBasisChange} noun="visits" prefix="er-date" inGroup={inGroup}
      rangeLabel={rangeLabel(range)} presets={presets} rangeKey={rangeKey}
      onPickRange={(k) => onChange({ kind: "preset", key: k })}
      addedTip="Filtering by the date each visit was added to revQ" dosTip="Filtering by date of service"
      custom={{ selected: range.kind === "custom", render: (close) => <CalendarPop onApply={(r) => { onChange(r); close(); }} onCancel={close} /> }}
    />
  );
}
